import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { SlingoLang } from "@lib/titan2-slingo/dist/game/view/abstract/lang/SlingoLang";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { MessageData } from "@lib/titan2/dist/game/model/data/MessageData";
import { CommonLang } from "@lib/titan2/dist/game/view/lang/CommonLang";
import { KeyValuePair } from "@lib/titan2/dist/util/collection/KeyValuePair";
import { EventHandler } from "@lib/titan2/dist/util/event/EventHandler";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { RandomUtil } from "@lib/titan2/dist/util/random/RandomUtil";
import { Timer } from "@lib/titan2/dist/util/time/Timer";
import { gsap } from "gsap";
import { GameEvent, ScatterPickEvent } from "../../../controller/event/GameEvent";
import { getYouCouldWinText } from "../../../utils/TaggedText/YouCouldWinFactory";
import { GameLang } from "../lang/GameLang";
/**
 * GameMessageBar.ts
 */
export class GameMessageBar extends AbstractGameView {
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(SlingoEvent.SPIN_RESULT, this.handleSpinResult);
        this.registerGame(SlingoEvent.DAB_ACTIVE, this.handleDabActive);
        this.registerGame(GameEvent.COIN_WIN_ANIMATION, this.handleScatterWinCoin);
        this.registerGame(SlingoEvent.AVAILABLE_WIN, this.handleAvailableWin);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_END, this.handleAvailableWinEnd);
        this.registerGame(CommonGameEvent.COMPLETE, this.handleComplete);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(ScatterPickEvent.TRANSITION_IN, this.handleCococashBonusEnter);
        this.registerGame(ScatterPickEvent.PICK_RESULT, this.handleCococashBonusWon);
    }
    create() {
        super.create();
        this.data = this.factory.createMessageBarData();
        this.cycling = false;
        this.maxLength = this.uiProperties.getMaxLength();
        this.createMessageField();
        this.createMessages();
        this.createMessageTimer();
        Logger.addObjectRef(this, "GameMessageBar");
    }
    createMessageField() {
        this.messageField = this.addChild(getYouCouldWinText(this.factory, this.landscapeProperties.messageFieldProperties, this.portraitProperties?.messageFieldProperties));
        this.addChild(this.messageField);
    }
    createMessages() {
        this.idleMessageKeys = this.createMessageKeys(GameLang.IDLE, this.data.idleMessages);
        this.idleStandardSpinMessageKeys = this.createMessageKeys(GameLang.IDLE_STANDARD_SPIN, this.data.idleStandardSpinMessages);
        this.idleFreeSpinMessageKeys = this.createMessageKeys(GameLang.IDLE_FREE_SPIN, this.data.idleFreeSpinMessages);
        this.chooseCellMessageKeys = this.createMessageKeys(GameLang.CHOOSE_CELL, this.data.chooseCellMessages);
    }
    createMessageTimer() {
        this.messageTimer = new Timer(this.uiProperties.messageInterval);
        this.messageTimer.onUpdate(this.handleMessageTimerUpdated, this);
    }
    handleCococashBonusEnter() {
        this.setSingle(this.singleMessage(GameLang.SCATTER_GAME1));
    }
    handleCococashBonusWon() {
        this.setSingle(this.singleMessage(GameLang.SCATTER_GAME3));
    }
    handlePurchaseEntry() {
        this.setCycle(this.idleMessageKeys, this.cycleIdleMessage);
    }
    handleSpinResult() {
        let state = this.model.getPreviousState();
        if (state.isStandardSpin()) {
            this.setSingle(this.singleStandardSpinMessage());
        }
        else if (state.isFreeSpin()) {
            this.setSingle(this.singleMessage(GameLang.FREE_SPIN));
        }
        else if (state.isPurchaseSpin()) {
            this.setSingle(this.singleMessage(GameLang.PURCHASED_SPIN));
        }
    }
    handleFreeSpinStart() {
        this.setSingle((this.singleMessage(GameLang.REEL_STRIKE_FREE_SPIN)));
    }
    handleFreeSpinRetrigger() {
        this.setSingle((this.singleMessage(GameLang.REEL_STRIKE_FREE_SPIN)));
    }
    handleDabActive() {
        let dabData = this.model.getDabData();
        if (dabData.hasSuperJokerCellToDab()) {
            this.setSingle(this.singleMessage(GameLang.AUTO_SUPER_WILD));
        }
        else if (dabData.hasJokerCellToDab()) {
            this.setSingle(this.singleMessage(GameLang.AUTO_WILD));
        }
        else {
            this.setCycle(this.chooseCellMessageKeys, this.cycleMessage);
        }
    }
    handleFreeSpin() {
        this.setCycle(this.idleFreeSpinMessageKeys, this.cycleMessage);
    }
    handleAvailableWin() {
        //Oh boy. So because this game does not use numbers on the ticket, we are using a custom implementation of text 
        // (ImageTaggedText) in order to show images inside of the message.
        //In order to do that, we must do a bit of prep work with the key to add the "tags"
        const availableWin = this.model.getAvailableWin();
        const keys = availableWin.getKey();
        const keyStructured = keys.split(',').map(x => `{${x}}`).join('');
        const params = [
            new KeyValuePair(GameLang.PARAM_CELLS, keyStructured),
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(availableWin.getWinAmount()))
        ];
        const message = this.langManager.getLang(GameLang.PURCHASE_SPIN_1, params); //We cant use the length because we have added extra token data
        this.messageField.setText(message);
    }
    handleAvailableWinEnd() {
        this.clearMessages();
    }
    handleScatterWinJoker() {
        this.setSingle(this.singleScatterWinJokerMessage());
    }
    handleScatterWinCoin() {
        this.setSingle(this.singleScatterWinCoinMessage());
    }
    handleSlingo() {
        this.clearMessages();
    }
    handleComplete() {
        this.clearMessages();
    }
    handleStandardSpinRecovery() {
        if (this.model.isSpin()) {
            this.setSingle(this.singleMessage(`${GameLang.IDLE_STANDARD_SPIN}1`));
        }
    }
    handleFreeSpinRecovery() {
        if (this.model.isSpin()) {
            this.setSingle(this.singleMessage(`${GameLang.IDLE_FREE_SPIN}1`));
        }
    }
    handleMessageTimerUpdated() {
        if (this.currentMessage.hasNext()) {
            this.updateMessage(this.currentMessage);
        }
        else {
            if (this.cycling) {
                let index = this.messageCycleIndex + 1;
                this.messageCycleIndex = index < this.messagesToCycle.length ? index : 0;
                this.nextMessage();
            }
            else {
                this.messageTimer.stop();
            }
        }
    }
    setSingle(message) {
        this.messageTimer.stop();
        this.cycling = false;
        this.updateMessage(message);
        this.messageTimer.start();
    }
    setCycle(messages, action, updateMessage = true) {
        this.messageTimer.stop();
        this.cycling = true;
        this.messagesToCycle = messages;
        this.messageCycleIndex = 0;
        this.cycleMessageHandler = new EventHandler(action, this);
        this.messageTimer.start();
        if (updateMessage) {
            this.nextMessage();
        }
    }
    clearMessages() {
        this.messageTimer.stop();
        this.cycling = false;
        this.messagesToCycle = [];
        this.cycleMessageHandler = null;
        this.updateMessage(new MessageData("", 10));
    }
    nextMessage() {
        let key = this.messagesToCycle[this.messageCycleIndex];
        if (this.cycleMessageHandler) {
            this.cycleMessageHandler.call(key);
        }
    }
    updateMessage(message) {
        this.currentMessage = message;
        if (Service.isReplay()) {
            this.messageField.setText(this.langManager.getLang("GameLang.OPTIONS_DGH"));
            this.messageField.alpha = 1;
        }
        else {
            this.messageField.alpha = 0;
            this.messageField.setText(this.currentMessage.next());
            gsap.to(this.messageField, { alpha: 1, duration: 0.5 });
        }
    }
    cycleMessage(key) {
        this.updateMessage(this.createMessageData(key));
    }
    cycleIdleMessage(key) {
        const gameConfig = this.model.getGameConfig();
        const maxWin = this.formatCurrency(this.model.getMaxWin());
        const scatter5 = this.formatCurrency(this.model.getScatterPrize(5));
        const scatter4 = this.formatCurrency(this.model.getScatterPrize(4));
        const scatter3 = this.formatCurrency(this.model.getScatterPrize(3));
        const gameName = this.model.getGameId() === "slingo-irishxxxtreme" ? "Irishxxxtreme" : null;
        let params = [
            new KeyValuePair(GameLang.PARAM_GAME, gameName),
            new KeyValuePair(SlingoLang.PARAM_SPINS, gameConfig.getNumberOfSpins()),
            new KeyValuePair(CommonLang.PARAM_AMOUNT, maxWin),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_5, scatter5),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_4, scatter4),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_3, scatter3)
        ];
        this.updateMessage(this.createMessageData(key, params));
    }
    cycleIdleStandardSpinMessage(key) {
        let config = this.model.getGameProgress();
        if (config) {
            let params = [
                new KeyValuePair(SlingoLang.PARAM_SPINS, config.getSpinsRemaining())
            ];
            this.updateMessage(this.createMessageData(key, params));
        }
    }
    singleMessage(key) {
        return this.createMessageData(key);
    }
    singleStandardSpinMessage() {
        let gameConfig = this.model.getGameConfig();
        let progress = this.model.getGameProgress();
        let standardSpins = progress.getStandardSpins();
        let numberOfSpins = standardSpins > 0 ? standardSpins : gameConfig.getNumberOfSpins();
        let currentSpin = numberOfSpins - progress.getSpinsRemaining();
        let params = [
            new KeyValuePair(SlingoLang.PARAM_SPIN, currentSpin),
            new KeyValuePair(SlingoLang.PARAM_SPINS, numberOfSpins)
        ];
        return this.createMessageData(SlingoLang.STANDARD_SPIN, params);
    }
    singleScatterWinJokerMessage() {
        const scatterWin = this.model.getSpinResult()?.getSymbolWins()?.find(win => win.getType() === undefined);
        const params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(scatterWin?.getAmount() ?? 0)),
        ];
        const key = SlingoLang.SYMBOL_WIN + (scatterWin?.getNumberOfSymbols() ?? 0) + SlingoLang.SYMBOL_WIN_JOKER + 0;
        return this.createMessageData(key, params);
    }
    singleScatterWinCoinMessage() {
        let result = this.model.getSpinResult();
        let win = result.getCoinWin();
        let params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(win.getAmount()))
        ];
        let key = SlingoLang.SYMBOL_WIN_COIN + 0;
        return this.createMessageData(key, params);
    }
    singleDevilMessage() {
        return this.createMessageData(GameLang.DEVIL + RandomUtil.nextInt(0, 2));
    }
    singleAvailableWinMessage(key) {
        const availableWin = this.model.getAvailableWin();
        let params = [
            new KeyValuePair(GameLang.PARAM_CELLS, availableWin.getKey()),
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(availableWin.getWinAmount()))
        ];
        return this.createMessageData(key, params);
    }
    singleSlingoCompleteMessage(patternWin) {
        let params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(patternWin))
        ];
        return this.createMessageData(GameLang.SLINGO_COMPLETE, params);
    }
    createMessageKeys(key, total) {
        let keys = [];
        for (let i = 0; i < total; i++) {
            keys.push(key + i);
        }
        return keys;
    }
    createMessageData(key, params) {
        return new MessageData(this.langManager.getLang(key, params), this.maxLength);
    }
}
//# sourceMappingURL=GameMessageBar.js.map